import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, HardHat, Users } from "lucide-react";
import api from "../lib/api";
import { usePincodeLookup } from "../lib/usePincodeLookup";
import FileUpload from "../components/FileUpload";
import NccBrandedHeader from "../components/NccBrandedHeader";
import PayrollEmployeePage from "./PayrollEmployeePage";

// ============================================================
// RegisterPage — top-level wrapper with a 2-way toggle between
//   • Contractor Registration (default existing flow)
//   • Payroll Manpower Registration (existing /register/payroll flow)
// ============================================================
export default function RegisterPage() {
  const [sp, setSp] = useSearchParams();
  // Read ?as=payroll from URL, default to "contractor"
  const initial = (sp.get("as") || "contractor").toLowerCase() === "payroll" ? "payroll" : "contractor";
  const [mode, setMode] = useState(initial);

  function switchMode(next) {
    setMode(next);
    const newSp = new URLSearchParams(sp);
    if (next === "payroll") newSp.set("as", "payroll");
    else newSp.delete("as");
    setSp(newSp, { replace: true });
  }

  return (
    <div data-testid="register-wrapper">
      {/* Mode toggle bar */}
      <div className="border-b-2 border-slate-900 bg-ncc-navy px-4 py-3 md:px-8">
        <div className="mx-auto flex max-w-screen-2xl flex-wrap items-center gap-3">
          <div className="industrial-label text-ncc-gold2">// Register as</div>
          <div className="inline-flex overflow-hidden rounded-md border-2 border-ncc-gold">
            <button
              type="button"
              onClick={() => switchMode("contractor")}
              data-testid="register-toggle-contractor"
              className={`inline-flex items-center gap-1.5 px-4 py-2 industrial-label text-xs transition-colors ${
                mode === "contractor" ? "bg-ncc-gold text-ncc-navy" : "bg-transparent text-white hover:bg-white/10"
              }`}
            >
              <HardHat className="h-4 w-4" /> Contractor
            </button>
            <button
              type="button"
              onClick={() => switchMode("payroll")}
              data-testid="register-toggle-payroll"
              className={`inline-flex items-center gap-1.5 px-4 py-2 industrial-label text-xs transition-colors ${
                mode === "payroll" ? "bg-ncc-gold text-ncc-navy" : "bg-transparent text-white hover:bg-white/10"
              }`}
            >
              <Users className="h-4 w-4" /> Payroll Manpower
            </button>
          </div>
          <span className="ml-auto font-mono text-[11px] text-white/70">
            {mode === "contractor" ? "Register as a Labour Contractor" : "Register as NCC Payroll / Manpower Employee"}
          </span>
        </div>
      </div>

      {mode === "payroll" ? <PayrollEmployeePage /> : <ContractorRegisterForm />}
    </div>
  );
}

function ContractorRegisterForm() {
  const nav = useNavigate();
  const [trades, setTrades] = useState([]);
  const [contractorTypes, setContractorTypes] = useState([]);
  const [idProofTypes, setIdProofTypes] = useState(["PAN", "Aadhar"]);
  const [nccSites, setNccSites] = useState([]);
  const [form, setForm] = useState({
    name: "", contact_phone: "", contact_email: "",
    village: "", pincode: "", city: "", state: "", contractor_type: "Labour Contractor",
    current_status: "NEW", ncc_site_name: "",
    id_proof_type: "PAN", id_proof_number: "",
    referred_by: "", referred_by_name: "", referred_by_phone: "",
    bio: "", trade_counts: {},
    experience_years: 0,
    password: "", password_confirm: "",
    photo: null, id_docs: [], certifications: [], projects: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  // Inline duplicate checks: status = "idle" | "checking" | "available" | "taken"
  const [dup, setDup] = useState({ phone: "idle", email: "idle", id_proof: "idle" });
  const dupTimers = useRef({});

  const { pinLoading, pinSuggestions, onPincodeChange } = usePincodeLookup(({ pincode, city, state }) => {
    setForm((f) => ({
      ...f,
      pincode: pincode ?? f.pincode,
      city: f.city || city || "",
      state: f.state || state || "",
    }));
  });

  const selectedTrades = Object.keys(form.trade_counts);
  const crewSize = Object.values(form.trade_counts).reduce((a, b) => a + (Number(b) || 0), 0);
  const blankTrades = selectedTrades.filter((t) => !form.trade_counts[t] || Number(form.trade_counts[t]) < 1);
  const hasDuplicates = dup.phone === "taken" || dup.email === "taken" || dup.id_proof === "taken";

  function checkDuplicate(field, value) {
    if (dupTimers.current[field]) clearTimeout(dupTimers.current[field]);
    if (!value || !value.trim()) {
      setDup((d) => ({ ...d, [field]: "idle" }));
      return;
    }
    setDup((d) => ({ ...d, [field]: "checking" }));
    dupTimers.current[field] = setTimeout(async () => {
      try {
        const r = await api.get("/contractors/check-duplicate", { params: { field, value } });
        setDup((d) => ({ ...d, [field]: r.data.available ? "available" : "taken" }));
      } catch {
        setDup((d) => ({ ...d, [field]: "idle" }));
      }
    }, 450);
  }

  useEffect(() => {
    api.get("/meta/trades").then((r) => {
      setTrades(r.data.trades || []);
      setContractorTypes(r.data.contractor_types || []);
      const ipt = r.data.id_proof_types || [];
      if (ipt.length > 0) setIdProofTypes(ipt);
      setNccSites(r.data.ncc_sites || []);
      // align defaults with masters
      setForm((f) => ({
        ...f,
        contractor_type: (r.data.contractor_types || []).includes(f.contractor_type)
          ? f.contractor_type
          : (r.data.contractor_types || [])[0] || f.contractor_type,
        id_proof_type: (ipt.length > 0 && !ipt.includes(f.id_proof_type)) ? ipt[0] : f.id_proof_type,
      }));
    }).catch(() => {});
  }, []);

  function toggleTrade(t) {
    setForm((f) => {
      const next = { ...f.trade_counts };
      if (t in next) delete next[t]; else next[t] = "";
      return { ...f, trade_counts: next };
    });
  }

  function setTradeCount(t, n) {
    // allow blank while typing; clamp positives only
    if (n === "" || n === null || n === undefined) {
      setForm((f) => ({ ...f, trade_counts: { ...f.trade_counts, [t]: "" } }));
      return;
    }
    const num = Math.max(1, Number(n) || 1);
    setForm((f) => ({ ...f, trade_counts: { ...f.trade_counts, [t]: num } }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (selectedTrades.length === 0) {
      setError("Please select at least one trade");
      return;
    }
    if (blankTrades.length > 0) {
      setError(`Please enter the number of workers (Nos.) for: ${blankTrades.join(", ")}`);
      return;
    }
    if (hasDuplicates) {
      const which = [];
      if (dup.phone === "taken") which.push("phone");
      if (dup.email === "taken") which.push("email");
      if (dup.id_proof === "taken") which.push(form.id_proof_type || "PAN/Aadhar");
      setError(`This ${which.join(" / ")} is already registered. Please use a different one.`);
      return;
    }
    if (form.current_status === "NCCL" && !form.ncc_site_name.trim()) {
      setError("Please enter the NCC Site Name");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        ...form,
        contact_email: form.contact_email || null,
        location: [form.city, form.state].filter(Boolean).join(", "),
        trades: selectedTrades,
        trade_counts: Object.fromEntries(selectedTrades.map((t) => [t, Number(form.trade_counts[t])])),
        crew_size: crewSize,
        experience_years: Number(form.experience_years),
        referred_by: form.referred_by || null,
        referred_by_name: form.referred_by ? (form.referred_by_name || null) : null,
        referred_by_phone: form.referred_by ? (form.referred_by_phone || null) : null,
      };
      await api.post("/contractors", body);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed";
      setError(detail);
      // scroll the error into view
      setTimeout(() => {
        const el = document.querySelector('[data-testid="reg-error"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 md:px-8">
        <div className="border-2 border-slate-900 bg-white p-8 text-center shadow-brutal">
          <div className="mx-auto flex h-14 w-14 items-center justify-center border-2 border-slate-900 bg-safety-500">
            <CheckCircle2 className="h-7 w-7 text-slate-900" />
          </div>
          <h1 className="mt-6 font-heading text-3xl font-black uppercase tracking-tight">Submission received</h1>
          <p className="mt-3 text-slate-700">Your profile is pending verification by our admin team. You&apos;ll be visible in the directory once approved.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button onClick={() => nav("/")} data-testid="registered-home-btn" className="border-2 border-slate-900 bg-safety-500 px-4 py-2 industrial-label hover:-translate-y-0.5 hover:shadow-brutal-sm transition-all">Back to home</button>
            <button onClick={() => { setDone(false); setForm({ name: "", contact_phone: "", contact_email: "", village: "", pincode: "", city: "", state: "", contractor_type: "Labour Contractor", current_status: "NEW", ncc_site_name: "", id_proof_type: "PAN", id_proof_number: "", referred_by: "", referred_by_name: "", referred_by_phone: "", bio: "", trade_counts: {}, experience_years: 0, photo: null, id_docs: [], certifications: [], projects: [] }); }} className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label hover:-translate-y-0.5 hover:shadow-brutal-sm transition-all">Register another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 md:px-8">
      <NccBrandedHeader subtitle="Registration" tagline="Fill in details below. Your profile goes live after admin approval." />

      <form onSubmit={submit} className="mt-8 space-y-8" data-testid="register-form">
        <FieldGroup title="Basic Information">
          <Grid>
            <TField label="Full Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="reg-name" />
            <TField label="Phone" required value={form.contact_phone}
              onChange={(v) => { setForm({ ...form, contact_phone: v }); checkDuplicate("phone", v); }}
              testid="reg-phone"
              status={dup.phone}
              statusMessage="This phone number is already registered. Please use a different one." />
            <label className="block">
              <Label>Pincode</Label>
              <div className="relative">
                <input
                  value={form.pincode}
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 6); setForm((f) => ({ ...f, pincode: v })); onPincodeChange(v); }}
                  placeholder="6 digits"
                  data-testid="reg-pincode"
                  className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 pr-9 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
                {pinLoading && <Loader2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
              </div>
              {pinSuggestions.length > 0 && (
                <div className="mt-1 font-mono text-[10px] text-slate-500">
                  {pinSuggestions.length} post offices found · use Village dropdown to pick
                </div>
              )}
            </label>
            <label className="block">
              <Label>Village</Label>
              <input
                list="reg-village-suggestions"
                value={form.village}
                onChange={(e) => setForm({ ...form, village: e.target.value })}
                placeholder={pinSuggestions.length ? "Pick or type…" : ""}
                data-testid="reg-village"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
              />
              <datalist id="reg-village-suggestions">
                {pinSuggestions.map((p) => <option key={p} value={p} />)}
              </datalist>
            </label>
            <TField label="City" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="reg-city" />
            <TField label="State" required value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="reg-state" />
            <TField label="Email" type="email" value={form.contact_email}
              onChange={(v) => { setForm({ ...form, contact_email: v }); checkDuplicate("email", v); }}
              testid="reg-email"
              status={dup.email}
              statusMessage="This email is already registered. Please use a different one." />
            <TField label="Experience (years)" type="number" value={form.experience_years} onChange={(v) => setForm({ ...form, experience_years: v })} testid="reg-exp" />
            <label className="block md:col-span-2">
              <Label>Contractor Type <span className="text-safety-600">*</span></Label>
              <select
                required
                value={form.contractor_type}
                onChange={(e) => setForm({ ...form, contractor_type: e.target.value })}
                data-testid="reg-contractor-type"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
              >
                {contractorTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <div className="md:col-span-2">
              <Label>Current Status <span className="text-safety-600">*</span></Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label
                  className={`flex cursor-pointer items-center gap-3 border-2 px-4 py-3 transition-all ${form.current_status === "NCCL" ? "border-slate-900 bg-safety-500 shadow-brutal-sm" : "border-slate-300 bg-white hover:border-slate-900"}`}
                  data-testid="reg-status-nccl"
                >
                  <input
                    type="radio"
                    name="current_status"
                    value="NCCL"
                    checked={form.current_status === "NCCL"}
                    onChange={() => setForm({ ...form, current_status: "NCCL" })}
                    className="h-4 w-4 accent-slate-900"
                  />
                  <div>
                    <div className="industrial-label text-slate-900">NCCL</div>
                    <div className="font-mono text-xs text-slate-700">Currently engaged with NCC</div>
                  </div>
                </label>
                <label
                  className={`flex cursor-pointer items-center gap-3 border-2 px-4 py-3 transition-all ${form.current_status === "NEW" ? "border-slate-900 bg-safety-500 shadow-brutal-sm" : "border-slate-300 bg-white hover:border-slate-900"}`}
                  data-testid="reg-status-new"
                >
                  <input
                    type="radio"
                    name="current_status"
                    value="NEW"
                    checked={form.current_status === "NEW"}
                    onChange={() => setForm({ ...form, current_status: "NEW", ncc_site_name: "" })}
                    className="h-4 w-4 accent-slate-900"
                  />
                  <div>
                    <div className="industrial-label text-slate-900">NEW</div>
                    <div className="font-mono text-xs text-slate-700">Fresh registration</div>
                  </div>
                </label>
              </div>
              {form.current_status === "NCCL" && (
                <div className="mt-3" data-testid="reg-ncc-site-wrapper">
                  <Label>NCC Site Name <span className="text-safety-600">*</span></Label>
                  <input
                    required
                    list="reg-ncc-site-list"
                    value={form.ncc_site_name}
                    onChange={(e) => setForm({ ...form, ncc_site_name: e.target.value })}
                    placeholder={nccSites.length > 0 ? "Select or type a site…" : "e.g. NCCL Hyderabad Metro Phase 2"}
                    data-testid="reg-ncc-site-name"
                    className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                  />
                  <datalist id="reg-ncc-site-list">
                    {nccSites.map((s) => <option key={s} value={s} />)}
                  </datalist>
                  {nccSites.length > 0 && (
                    <div className="mt-1 font-mono text-xs text-slate-500">
                      {nccSites.length} sites available · type to filter or add new
                    </div>
                  )}
                </div>
              )}
            </div>
            <label className="block md:col-span-2">
              <Label>PAN / Aadhar No.</Label>
              <div className="flex gap-2">
                <select
                  value={form.id_proof_type}
                  onChange={(e) => setForm({ ...form, id_proof_type: e.target.value })}
                  data-testid="reg-id-type"
                  className="w-32 border-2 border-slate-300 bg-slate-900 px-3 py-2 font-mono text-sm text-white focus:border-safety-500 focus:outline-none"
                >
                  {idProofTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="relative flex-1">
                  <input
                    value={form.id_proof_number}
                    onChange={(e) => { const v = e.target.value.toUpperCase(); setForm({ ...form, id_proof_number: v }); checkDuplicate("id_proof", v); }}
                    placeholder={form.id_proof_type === "PAN" ? "ABCDE1234F" : "1234 5678 9012"}
                    maxLength={form.id_proof_type === "PAN" ? 10 : 14}
                    data-testid="reg-id-number"
                    className={`w-full border-2 ${dup.id_proof === "taken" ? "border-red-500 focus:border-red-600" : dup.id_proof === "available" ? "border-emerald-500 focus:border-emerald-600" : "border-slate-300 focus:border-slate-900"} bg-slate-50 px-3 py-2 pr-9 font-mono text-sm tracking-wider focus:outline-none`}
                  />
                  {dup.id_proof === "checking" && (
                    <Loader2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" data-testid="reg-id-number-checking" />
                  )}
                  {dup.id_proof === "available" && (
                    <CheckCircle2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" data-testid="reg-id-number-ok" />
                  )}
                  {dup.id_proof === "taken" && (
                    <XCircle className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500" data-testid="reg-id-number-taken" />
                  )}
                </div>
              </div>
              {dup.id_proof === "taken" ? (
                <div className="mt-1 font-mono text-xs text-red-600" data-testid="reg-id-number-taken-msg">
                  This {form.id_proof_type} number is already registered. Please verify and try again.
                </div>
              ) : (
                <div className="mt-1 font-mono text-xs text-slate-500">
                  {form.id_proof_type === "PAN" ? "10 chars · e.g. ABCDE1234F" : "12 digits · e.g. 1234 5678 9012"}
                </div>
              )}
            </label>
            <div className="md:col-span-2" data-testid="reg-referred-by-wrapper">
              <Label>Referred by <span className="font-mono text-xs text-slate-500">(optional)</span></Label>
              <select
                value={form.referred_by}
                onChange={(e) => setForm({ ...form, referred_by: e.target.value, referred_by_name: e.target.value ? form.referred_by_name : "", referred_by_phone: e.target.value ? form.referred_by_phone : "" })}
                data-testid="reg-referred-by"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
              >
                <option value="">— None —</option>
                <option value="NCC Employee">NCC Employee</option>
                <option value="Agency">Agency</option>
                <option value="Contractor">Contractor</option>
                <option value="Source / Others">Source / Others</option>
              </select>
              {form.referred_by && (
                <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="reg-referred-by-details">
                  <label className="block">
                    <Label>Referrer Name</Label>
                    <input
                      value={form.referred_by_name}
                      onChange={(e) => setForm({ ...form, referred_by_name: e.target.value })}
                      data-testid="reg-referred-by-name"
                      placeholder="Full name of referrer"
                      className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <Label>Referrer Number</Label>
                    <input
                      value={form.referred_by_phone}
                      onChange={(e) => setForm({ ...form, referred_by_phone: e.target.value })}
                      data-testid="reg-referred-by-phone"
                      placeholder="10-digit mobile"
                      inputMode="tel"
                      className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                    />
                  </label>
                </div>
              )}
            </div>
          </Grid>
          <div className="mt-4">
            <Label>About / Bio</Label>
            <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="reg-bio"
              className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none" />
          </div>
        </FieldGroup>

        <FieldGroup title={`Trades & Capacity${crewSize > 0 ? ` · ${crewSize} (Auto Nos)` : ""}`}>
          <div className="industrial-label text-slate-500 mb-2">Select trades and enter the number of workers (Nos.) for each</div>
          <div className="flex flex-wrap gap-2">
            {trades.map((t) => {
              const on = t in form.trade_counts;
              return (
                <div
                  key={t}
                  data-testid={`trade-row-${t}`}
                  className={`flex items-center gap-2 border-2 border-slate-900 transition-all ${on ? "bg-safety-500 shadow-brutal-sm -translate-y-0.5" : "bg-white hover:bg-slate-50"}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleTrade(t)}
                    data-testid={`trade-toggle-${t}`}
                    className="px-3 py-1.5 industrial-label text-slate-900"
                  >
                    {t}
                  </button>
                  {on && (
                    <>
                      <span className="industrial-label text-slate-900/70 border-l-2 border-slate-900 pl-2 pr-1">Nos</span>
                      <input
                        type="number"
                        min="1"
                        value={form.trade_counts[t] ?? ""}
                        onChange={(e) => setTradeCount(t, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="—"
                        data-testid={`trade-count-${t}`}
                        className="w-16 border-l-2 border-slate-900 bg-white px-2 py-1.5 font-mono text-sm focus:bg-slate-50 focus:outline-none"
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <input type="hidden" data-testid="reg-crew-size" value={crewSize} readOnly />
        </FieldGroup>

        <FieldGroup title="Photo & Documents">
          <div className="grid gap-6 md:grid-cols-3">
            <div>
              <Label>Profile Photo</Label>
              <FileUpload value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} accept="image/*" allowCamera testid="reg-photo" label="Upload photo" />
            </div>
            <div>
              <Label>ID / License</Label>
              <FileUpload value={form.id_docs} onChange={(v) => setForm({ ...form, id_docs: v })} multiple label="Upload IDs" />
            </div>
            <div>
              <Label>Certifications</Label>
              <FileUpload value={form.certifications} onChange={(v) => setForm({ ...form, certifications: v })} multiple label="Upload certs" />
            </div>
          </div>
        </FieldGroup>

        {error && (
          <div
            data-testid="reg-error"
            className="flex items-start gap-3 border-2 border-red-600 bg-red-50 p-4 shadow-brutal-sm"
            role="alert"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center border-2 border-red-600 bg-red-600 font-mono text-sm font-bold text-white">!</div>
            <div>
              <div className="industrial-label text-red-700">Duplicate · Cannot Save</div>
              <div className="mt-1 font-mono text-sm text-red-700">{error}</div>
            </div>
          </div>
        )}

        <button type="submit" disabled={submitting || hasDuplicates}
          data-testid="reg-submit-btn"
          title={hasDuplicates ? "Resolve duplicate fields before submitting" : undefined}
          className="flex items-center gap-2 border-2 border-slate-900 bg-safety-500 px-6 py-3 industrial-label text-slate-900 transition-all hover:-translate-y-0.5 hover:shadow-brutal disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none disabled:cursor-not-allowed">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {submitting ? "Submitting" : hasDuplicates ? "Resolve duplicates above" : "Submit for Approval"}
        </button>
      </form>
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="border-2 border-slate-900 bg-white p-6">
      <div className="border-b-2 border-slate-900 pb-2">
        <div className="industrial-label">{title}</div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Grid({ children, className = "" }) {
  return <div className={`grid gap-4 md:grid-cols-2 ${className}`}>{children}</div>;
}

function Label({ children }) {
  return <div className="industrial-label mb-1 text-slate-600">{children}</div>;
}

function TField({ label, value, onChange, type = "text", required, testid, status, statusMessage }) {
  // status: "idle" | "checking" | "available" | "taken"
  const borderColor =
    status === "taken" ? "border-red-500 focus:border-red-600"
    : status === "available" ? "border-emerald-500 focus:border-emerald-600"
    : "border-slate-300 focus:border-slate-900";
  return (
    <label className="block">
      <Label>{label}{required && <span className="text-safety-600"> *</span>}</Label>
      <div className="relative">
        <input
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`w-full border-2 ${borderColor} bg-slate-50 px-3 py-2 pr-9 font-mono text-sm focus:outline-none`}
        />
        {status === "checking" && (
          <Loader2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" data-testid={`${testid}-checking`} />
        )}
        {status === "available" && (
          <CheckCircle2 className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" data-testid={`${testid}-ok`} />
        )}
        {status === "taken" && (
          <XCircle className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-red-500" data-testid={`${testid}-taken`} />
        )}
      </div>
      {status === "taken" && (
        <div className="mt-1 font-mono text-xs text-red-600" data-testid={`${testid}-taken-msg`}>
          {statusMessage || "Already registered. Please use a different one."}
        </div>
      )}
    </label>
  );
}
